import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

import { NamekError } from "@/models";
import { axiosInstanceNamek } from "@/service/Axios";

type Status = number;

export class DownloadError extends NamekError {}

type DownloadFileOptions = {
  fileName?: string;
  defaultFileName?: string;
  axiosInstance?: AxiosInstance; // axiosInstanceNamek by default
  configRequest?: AxiosRequestConfig;
};

/**
 * Download a file util for a Namek Context
 *
 * - By default, the name of the file is extracted from the header response.
 * - The fileName option can be used to set the name of the file
 * - The defaultFileName can be used if the header response does not contain a file name
 * - By default, the axiosInstanceName is used. The axiosInstance option can be used to change it.
 *
 * @param url
 * @param DownloadFileOptions Options
 */
export const downloadFile = async (
  url: string,
  {
    defaultFileName,
    fileName,
    axiosInstance = axiosInstanceNamek,
    configRequest = {},
  }: DownloadFileOptions = {},
): Promise<Status> => {
  try {
    const response = await axiosInstance.get<ArrayBuffer>(url, {
      responseType: "arraybuffer",
      ...configRequest,
    });

    fileName = fileName ?? determineFileName(response, defaultFileName);

    const blob = new Blob([response.data]);
    const urlBlob = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = urlBlob;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);

    link.click();
    return response.status;
  } catch (e: unknown) {
    throw parseError(e);
  }
};

const parseError = (exception: unknown) => {
  try {
    // Crear un DataView para acceder a los datos del ArrayBuffer
    // @ts-expect-error asd
    const dataView = new DataView(exception.response?.data);
    const jsonString = new TextDecoder().decode(dataView);
    const responseData = JSON.parse(jsonString);
    return new DownloadError(responseData);
  } catch (e: unknown) {
    return exception;
  }
};

const determineFileName = (response: AxiosResponse, defaultFileName?: string): string => {
  const headerStringFileName: string = response.headers["content-disposition"];
  const regex = /filename=(.*?);/;
  const [, stringNameFileAndFormat] = headerStringFileName.match(regex)!;

  return stringNameFileAndFormat.replaceAll('"', "").trim() ?? defaultFileName;
};
